import React from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { FileText, Shield, Users, AlertTriangle, Scale, Mail } from 'lucide-react';

const TermsPage = () => {
  const { language } = useLanguage();

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-oar-blue via-oar-blue-dark to-oar-blue">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Termes et Conditions d'Utilisation
          </h1>
          <p className="text-white/80">
            Dernière mise à jour : Février 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-oar-bg">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12 space-y-8">
            
            {/* Introduction */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <Scale className="w-6 h-6" />
                1. Préambule
              </h2>
              <p className="text-oar-text leading-relaxed">
                Les présentes Conditions Générales d'Utilisation (ci-après "CGU") régissent l'accès et l'utilisation du site internet <strong>russophonie-afrique.com</strong> (ci-après "le Site"), édité par l'Organisation Africaine de la Russophonie (ci-après "OAR" ou "l'Association").
              </p>
              <p className="text-oar-text leading-relaxed mt-4">
                En accédant au Site et en utilisant ses services, vous acceptez sans réserve les présentes CGU. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser le Site.
              </p>
            </div>

            {/* Objet */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4">
                2. Objet du Site
              </h2>
              <p className="text-oar-text leading-relaxed">
                Le Site a pour objet de :
              </p>
              <ul className="list-disc list-inside text-oar-text space-y-2 mt-4 ml-4">
                <li>Présenter l'Organisation Africaine de la Russophonie, ses missions et activités</li>
                <li>Permettre l'adhésion à l'Association en tant que membre</li>
                <li>Faciliter les candidatures au Programme "10 000 Étudiants Russophones"</li>
                <li>Diffuser des informations sur la coopération Afrique-Russie</li>
                <li>Permettre l'inscription à la newsletter de l'Association</li>
                <li>Recevoir des dons pour soutenir les activités de l'Association</li>
              </ul>
            </div>

            {/* Éditeur */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4">
                3. Mentions Légales
              </h2>
              <div className="bg-oar-bg rounded-lg p-6">
                <p className="text-oar-text"><strong>Éditeur du Site :</strong></p>
                <p className="text-oar-text">Organisation Africaine de la Russophonie (OAR)</p>
                <p className="text-oar-text">Association à but non lucratif</p>
                <p className="text-oar-text">Siège social : Bangui, République Centrafricaine</p>
                <p className="text-oar-text mt-4"><strong>Contact :</strong></p>
                <p className="text-oar-text">Email : contact@russophonie-afrique.com</p>
                <p className="text-oar-text mt-4"><strong>Directeur de la publication :</strong></p>
                <p className="text-oar-text">Le Président de l'OAR</p>
              </div>
            </div>

            {/* Adhésion */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <Users className="w-6 h-6" />
                4. Conditions d'Adhésion
              </h2>
              <p className="text-oar-text leading-relaxed">
                L'adhésion à l'OAR est ouverte à toute personne physique majeure ou personne morale partageant les valeurs et objectifs de l'Association.
              </p>
              <h3 className="font-semibold text-oar-text mt-6 mb-2">4.1 Types d'adhésion</h3>
              <ul className="list-disc list-inside text-oar-text space-y-2 ml-4">
                <li><strong>Membre Standard (10 USD/an) :</strong> Accès aux services de base, newsletter, participation aux événements</li>
                <li><strong>Membre Premium (25 USD/an) :</strong> Tous les avantages Standard + accès prioritaire aux programmes, certificat de membre, carte de membre numérique</li>
              </ul>
              <h3 className="font-semibold text-oar-text mt-6 mb-2">4.2 Engagements du membre</h3>
              <p className="text-oar-text leading-relaxed">
                En devenant membre, vous vous engagez à :
              </p>
              <ul className="list-disc list-inside text-oar-text space-y-2 mt-2 ml-4">
                <li>Fournir des informations exactes et à jour</li>
                <li>Respecter les statuts et le règlement intérieur de l'Association</li>
                <li>Ne pas utiliser votre statut de membre à des fins illégales ou contraires aux objectifs de l'Association</li>
                <li>Respecter les autres membres et les valeurs de l'OAR</li>
              </ul>
            </div>

            {/* Propriété intellectuelle */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6" />
                5. Propriété Intellectuelle
              </h2>
              <p className="text-oar-text leading-relaxed">
                L'ensemble des éléments composant le Site (textes, images, logos, vidéos, graphismes, icônes, sons, logiciels, bases de données, etc.) est la propriété exclusive de l'OAR ou de ses partenaires et est protégé par les lois relatives à la propriété intellectuelle.
              </p>
              <p className="text-oar-text leading-relaxed mt-4">
                Toute reproduction, représentation, modification, publication, adaptation, totale ou partielle, des éléments du Site, quel que soit le moyen ou le procédé utilisé, est interdite sans l'autorisation écrite préalable de l'OAR.
              </p>
            </div>

            {/* Responsabilités */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                6. Limitation de Responsabilité
              </h2>
              <p className="text-oar-text leading-relaxed">
                L'OAR s'efforce d'assurer l'exactitude et la mise à jour des informations diffusées sur le Site. Toutefois, l'OAR ne peut garantir l'exactitude, la précision ou l'exhaustivité des informations mises à disposition.
              </p>
              <p className="text-oar-text leading-relaxed mt-4">
                L'OAR décline toute responsabilité :
              </p>
              <ul className="list-disc list-inside text-oar-text space-y-2 mt-2 ml-4">
                <li>Pour toute interruption du Site, survenance de bugs ou tout dommage résultant d'une intrusion frauduleuse d'un tiers</li>
                <li>Pour tout dommage direct ou indirect résultant de l'accès ou de l'utilisation du Site</li>
                <li>Pour le contenu des sites tiers vers lesquels des liens hypertextes peuvent renvoyer</li>
              </ul>
            </div>

            {/* Paiements */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4">
                7. Paiements et Remboursements
              </h2>
              <p className="text-oar-text leading-relaxed">
                Les paiements d'adhésion et les dons sont effectués via les moyens de paiement proposés sur le Site (carte bancaire via Paystack, cryptomonnaie USDT TRC20).
              </p>
              <p className="text-oar-text leading-relaxed mt-4">
                <strong>Politique de remboursement :</strong> Les cotisations d'adhésion et les dons ne sont pas remboursables, sauf en cas d'erreur technique avérée. Pour toute réclamation, veuillez contacter contact@russophonie-afrique.com dans un délai de 14 jours suivant le paiement.
              </p>
            </div>

            {/* Protection des données */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4">
                8. Protection des Données Personnelles
              </h2>
              <p className="text-oar-text leading-relaxed">
                La collecte et le traitement des données personnelles effectués sur le Site sont conformes au Règlement Général sur la Protection des Données (RGPD) et aux lois applicables en matière de protection des données.
              </p>
              <p className="text-oar-text leading-relaxed mt-4">
                Pour plus d'informations sur la manière dont nous collectons, utilisons et protégeons vos données personnelles, veuillez consulter notre <a href="/privacy" className="text-oar-blue hover:underline">Politique de Confidentialité</a>.
              </p>
            </div>

            {/* Modification des CGU */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4">
                9. Modification des CGU
              </h2>
              <p className="text-oar-text leading-relaxed">
                L'OAR se réserve le droit de modifier les présentes CGU à tout moment. Les modifications entrent en vigueur dès leur publication sur le Site. Il est conseillé aux utilisateurs de consulter régulièrement cette page.
              </p>
            </div>

            {/* Droit applicable */}
            <div>
              <h2 className="font-heading text-2xl font-bold text-oar-blue mb-4">
                10. Droit Applicable et Juridiction
              </h2>
              <p className="text-oar-text leading-relaxed">
                Les présentes CGU sont régies par le droit de la République Centrafricaine. En cas de litige relatif à l'interprétation ou à l'exécution des présentes, les parties s'efforceront de trouver une solution amiable. À défaut, le litige sera soumis aux tribunaux compétents de Bangui, République Centrafricaine.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-oar-blue-soft rounded-lg p-6 mt-8">
              <h2 className="font-heading text-xl font-bold text-oar-blue mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Contact
              </h2>
              <p className="text-oar-text">
                Pour toute question concernant les présentes CGU, vous pouvez nous contacter :
              </p>
              <p className="text-oar-text mt-2">
                <strong>Email :</strong> contact@russophonie-afrique.com
              </p>
              <p className="text-oar-text">
                <strong>Adresse :</strong> Organisation Africaine de la Russophonie, Bangui, République Centrafricaine
              </p>
            </div>

          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TermsPage;
